<template>
  <app-page-shell
    title="Activity Log"
    subtitle="Audit trail for user actions, module changes, and system activity."
  >
    <app-list-toolbar
      :per-page="perPage"
      :per-page-options="perPageOptions"
      @update:perPage="perPage = $event"
    >
      <template #filters>
        <v-select
          v-model="actionFilter"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="actionOptions"
          :reduce="option => option.value"
          label="label"
          placeholder="All Actions"
          class="action-filter-selector"
        />

        <v-select
          v-if="isSuperAdmin"
          v-model="userFilter"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="userOptions"
          :reduce="option => option.value"
          label="label"
          placeholder="All Users"
          class="user-filter-selector"
        />
      </template>

      <template #search>
        <b-form-input
          v-model="moduleFilter"
          class="app-toolbar-search"
          placeholder="Search module, description, or target ID..."
        />
      </template>
    </app-list-toolbar>

      <b-table
        ref="refActivityLogListTable"
        class="position-relative"
        :items="fetchActivityLogs"
        responsive
        :fields="tableColumns"
        primary-key="activity_log_id"
        show-empty
        empty-text="No matching records found"
        :busy="loading"
      >
        <template #table-busy>
          <b-skeleton-table
            :rows="perPage || 5"
            :columns="tableColumns ? tableColumns.length : 7"
            :table-props="{ bordered: true, striped: true }"
          />
        </template>

        <template #cell(time)="data">
          <span class="text-nowrap">{{ formatTime(data.item.activity_log_time) }}</span>
        </template>

        <template #cell(user)="data">
          <div
            v-if="data.item.user"
            class="text-nowrap"
          >
            <span class="font-weight-bold d-block">{{ data.item.user.user_fullname || '-' }}</span>
            <small class="text-muted">@{{ data.item.user.user_username }}</small>
          </div>
          <span v-else>{{ data.item.activity_log_user_id || '-' }}</span>
        </template>

        <template #cell(module)="data">
          <b-badge variant="light-secondary">
            {{ titleizeModule(data.item.activity_log_module) }}
          </b-badge>
        </template>

        <template #cell(action)="data">
          <b-badge :variant="resolveActionVariant(data.item.activity_log_action)">
            {{ resolveActionLabel(data.item.activity_log_action) }}
          </b-badge>
        </template>

        <template #cell(description)="data">
          <span
            class="text-truncate d-inline-block"
            style="max-width: 360px;"
          >
            {{ data.item.activity_log_description }}
          </span>
        </template>

        <template #cell(target)="data">
          <span>{{ data.item.activity_log_target_id || '-' }}</span>
        </template>

        <template #cell(ip)="data">
          <span>{{ data.item.activity_log_remote_ip || '-' }}</span>
        </template>
      </b-table>

    <app-pagination-footer
      :meta="dataMeta"
      :current-page="currentPage"
      :total-rows="totalActivityLogs"
      :per-page="perPage"
      @update:currentPage="currentPage = $event"
    />
  </app-page-shell>
</template>

<script>
import {
  BFormInput, BTable, BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import moment from 'moment-timezone'
import AppPageShell from '@/views/apps/components/AppPageShell.vue'
import AppListToolbar from '@/views/apps/components/AppListToolbar.vue'
import AppPaginationFooter from '@/views/apps/components/AppPaginationFooter.vue'

import useActivityLogList from './ActivityLogList'

export default {
  components: {
    BFormInput,
    BTable,
    BBadge,
    vSelect,
    AppPageShell,
    AppListToolbar,
    AppPaginationFooter,
  },
  setup() {
    const {
      fetchActivityLogs,
      fetchUsersForFilter,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalActivityLogs,
      dataMeta,
      perPageOptions,
      moduleFilter,
      actionFilter,
      userFilter,
      refActivityLogListTable,
      refetchData,
      isSuperAdmin,
      titleizeModule,
      resolveActionVariant,
      resolveActionLabel,
    } = useActivityLogList()

    const actionOptions = [
      { label: 'All Actions', value: null },
      { label: 'Create', value: 'create' },
      { label: 'Update', value: 'update' },
      { label: 'Delete', value: 'delete' },
      { label: 'Confirm', value: 'confirm' },
    ]

    return {
      fetchActivityLogs,
      fetchUsersForFilter,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalActivityLogs,
      dataMeta,
      perPageOptions,
      moduleFilter,
      actionFilter,
      userFilter,
      refActivityLogListTable,
      refetchData,
      isSuperAdmin,
      titleizeModule,
      resolveActionVariant,
      resolveActionLabel,
      actionOptions,
      userOptions: [],
    }
  },
  mounted() {
    if (this.isSuperAdmin) {
      this.fetchUsersForFilter().then(options => {
        this.userOptions = [{ label: 'All Users', value: null }, ...options]
      })
    }
  },
  methods: {
    formatTime(value) {
      if (!value) return '-'
      return moment(value).format('DD MMM YYYY HH:mm')
    },
  },
}
</script>

<style lang="scss" scoped>
.action-filter-selector {
  width: 180px;
}
.user-filter-selector {
  width: 220px;
}
.app-toolbar-search {
  max-width: 360px;
}

@media (max-width: 991.98px) {
  .action-filter-selector,
  .user-filter-selector,
  .app-toolbar-search {
    width: 100%;
    max-width: none;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
